/**
 * Email domain typo detection and suggestion utility.
 *
 * Detects common misspellings in popular email domains (e.g. gmail.comt,
 * hotmal.com) and suggests the correct domain. Uses Levenshtein distance
 * for fuzzy matching against a curated list of providers popular in Brazil.
 */

// Order matters: more common/global domains come first so that when two
// domains tie at the same Levenshtein distance, the more popular one wins.
const KNOWN_DOMAINS: readonly string[] = [
  // Global (most common first)
  'gmail.com',
  'hotmail.com',
  'outlook.com',
  'yahoo.com',
  'yahoo.com.br',
  'icloud.com',
  'live.com',
  'protonmail.com',
  'proton.me',
  'zoho.com',
  // Brazil-specific
  'bol.com.br',
  'uol.com.br',
  'terra.com.br',
  'globo.com',
  'globomail.com',
  'zipmail.com.br',
  'hotmail.com.br',
  'outlook.com.br',
  'live.com.br',
] as const;

/**
 * Minimum domain length (before TLD) to consider for server-side blocking.
 * Prevents false positives on very short domains like `rs.com` matching
 * known short entries.
 */
const MIN_DOMAIN_LENGTH_FOR_BLOCK = 8;

/** Maximum edit distance to consider a domain a likely typo. */
const MAX_DISTANCE = 2;

/**
 * Compute the Levenshtein edit distance between two strings.
 *
 * Uses the classic dynamic-programming approach with O(min(a,b)) space.
 */
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  // Keep the shorter string in `b` so the column array is smaller.
  if (a.length < b.length) {
    [a, b] = [b, a];
  }

  const bLen = b.length;
  const prev = Array.from({ length: bLen + 1 }, (_, i) => i);

  for (let i = 1; i <= a.length; i++) {
    let prevDiag = prev[0];
    prev[0] = i;

    for (let j = 1; j <= bLen; j++) {
      const temp = prev[j];
      if (a[i - 1] === b[j - 1]) {
        prev[j] = prevDiag;
      } else {
        prev[j] = 1 + Math.min(prevDiag, prev[j], prev[j - 1]);
      }
      prevDiag = temp;
    }
  }

  return prev[bLen];
}

/**
 * Extract the domain portion from an email address.
 *
 * @returns The lowercased domain, or `null` if the email is malformed.
 */
function extractDomain(email: string): {
  localPart: string;
  domain: string;
} | null {
  if (!email || !email.includes('@')) return null;

  const parts = email.split('@');
  if (parts.length !== 2) return null;

  const [localPart, domain] = parts;
  if (!localPart || !domain) return null;

  return { localPart, domain: domain.toLowerCase() };
}

/**
 * Find the closest known domain to the given domain.
 *
 * Scans the full `KNOWN_DOMAINS` list and returns the best match within
 * `MAX_DISTANCE`. Short-circuits once the best distance is 1 (cannot
 * improve further).
 */
function findClosestDomain(
  domain: string,
): { knownDomain: string; distance: number } | null {
  let bestDomain: string | null = null;
  let bestDistance = MAX_DISTANCE + 1;

  for (const knownDomain of KNOWN_DOMAINS) {
    // If we already have a distance-1 match, nothing can beat it.
    if (bestDistance === 1) break;

    const distance = levenshtein(domain, knownDomain);

    if (distance < bestDistance) {
      bestDistance = distance;
      bestDomain = knownDomain;
    }
  }

  if (bestDomain && bestDistance <= MAX_DISTANCE) {
    return { knownDomain: bestDomain, distance: bestDistance };
  }

  return null;
}

/**
 * Detect false `.br` suffix: domains like `gmail.com.br` where the base
 * domain (`gmail.com`) exists in the known list but the `.br` variant does not.
 *
 * @returns The correct domain without `.br`, or `null` if not a false suffix.
 */
function checkFalseBrSuffix(domain: string): string | null {
  if (!domain.endsWith('.br')) return null;

  // The .br version itself is a known domain — not a false suffix.
  if (KNOWN_DOMAINS.includes(domain)) return null;

  // Remove the `.br` suffix to get the base domain
  const baseDomain = domain.slice(0, -3);
  if (!baseDomain) return null;

  // Exact match: base is known (e.g. gmail.com.br → gmail.com)
  if (KNOWN_DOMAINS.includes(baseDomain)) {
    return baseDomain;
  }

  // Fuzzy match: base is a typo of a known domain
  // (e.g. iclod.com.br → iclod.com → distance 1 from icloud.com)
  const match = findClosestDomain(baseDomain);
  if (match && match.distance <= 1) {
    return match.knownDomain;
  }

  return null;
}

export interface EmailDomainSuggestion {
  /** The original email address as entered by the user. */
  original: string;
  /** The suggested corrected email address, or `null` if no typo detected. */
  suggested: string | null;
  /** The suggested correct domain, or `null` if no typo detected. */
  suggestedDomain: string | null;
  /** Whether a potential typo was detected. */
  hasTypo: boolean;
}

/**
 * Check an email address for common domain typos.
 *
 * @param email - The email address to check.
 * @returns An `EmailDomainSuggestion` with a corrected suggestion when a
 *   likely typo is detected, or `hasTypo: false` when the domain looks fine.
 *
 * @example
 * ```ts
 * checkEmailDomain('user@gmail.comt');
 * // → { hasTypo: true, suggested: 'user@gmail.com', suggestedDomain: 'gmail.com', original: 'user@gmail.comt' }
 *
 * checkEmailDomain('user@gmail.com');
 * // → { hasTypo: false, suggested: null, suggestedDomain: null, original: 'user@gmail.com' }
 * ```
 */
export function checkEmailDomain(rawEmail: string): EmailDomainSuggestion {
  const email = rawEmail.trim();
  const noSuggestion: EmailDomainSuggestion = {
    original: email,
    suggested: null,
    suggestedDomain: null,
    hasTypo: false,
  };

  const parsed = extractDomain(email);
  if (!parsed) return noSuggestion;

  const { localPart, domain } = parsed;

  // Exact match — no typo.
  if (KNOWN_DOMAINS.includes(domain)) {
    return noSuggestion;
  }

  // Detect false `.br` suffix: e.g. `gmail.com.br` → `gmail.com`.
  // If the domain ends with `.br` and removing it yields a known domain
  // (but the `.br` version is NOT known), it's a typo.
  const withoutBr = checkFalseBrSuffix(domain);
  if (withoutBr) {
    return {
      original: email,
      suggested: `${localPart}@${withoutBr}`,
      suggestedDomain: withoutBr,
      hasTypo: true,
    };
  }

  const match = findClosestDomain(domain);

  if (match) {
    return {
      original: email,
      suggested: `${localPart}@${match.knownDomain}`,
      suggestedDomain: match.knownDomain,
      hasTypo: true,
    };
  }

  return noSuggestion;
}

/**
 * Check whether an email domain is a known typo that should be blocked
 * server-side.
 *
 * This is a stricter variant for server-side validation — it only flags
 * domains with distance 1 from a known provider to minimise false positives.
 * Additionally, both the input domain and the matched known domain must be
 * at least {@link MIN_DOMAIN_LENGTH_FOR_BLOCK} characters long to avoid
 * false positives on short corporate domains (e.g. `rs.com`).
 *
 * @param email - The email address to validate.
 * @returns `true` if the domain appears to be a typo of a known provider.
 */
export function isLikelyDomainTypo(email: string): boolean {
  const parsed = extractDomain(email.trim());
  if (!parsed) return false;

  const { domain } = parsed;

  if (KNOWN_DOMAINS.includes(domain)) return false;

  // False `.br` suffix (e.g. gmail.com.br) is always a typo.
  if (checkFalseBrSuffix(domain)) return true;

  // Skip short domains to avoid false positives (e.g. rs.com ≠ r7.com).
  if (domain.length < MIN_DOMAIN_LENGTH_FOR_BLOCK) return false;

  return KNOWN_DOMAINS.some(
    (knownDomain) =>
      knownDomain.length >= MIN_DOMAIN_LENGTH_FOR_BLOCK &&
      levenshtein(domain, knownDomain) === 1,
  );
}
